import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { api, ISSUES } from "../lib/api";
import { Lock, KeyRound, SearchX, Copy, HelpCircle, MapPin, Loader2, ArrowLeft, ArrowRight, CheckCircle2, Check, Info } from "lucide-react";
import { toast, Toaster } from "sonner";

const ICONS = { Lock, KeyRound, SearchX, Copy, HelpCircle };
const BRANDS = ["Audi","BMW","Citroën","Dacia","Fiat","Ford","Honda","Hyundai","Kia","Mercedes","Nissan","Opel","Peugeot","Renault","Seat","Skoda","Tesla","Toyota","Volkswagen","Volvo","Otros"];

export default function Booking() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [taxIdStatus, setTaxIdStatus] = useState({ checking: false, valid: null });
  const [form, setForm] = useState({
    customer_type: "",
    tax_id: "",
    issue_type: "",
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_year: new Date().getFullYear(),
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    location: { lat: null, lng: null, address: "" },
    notes: "",
  });
  const [paymentOption, setPaymentOption] = useState("deposit");
  const [geocoding, setGeocoding] = useState({ checking: false, result: null });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (searchParams.get("cancelled")) toast.error("Pago cancelado. Puedes reintentarlo.");
  }, [searchParams]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateLocation = (patch) => setForm((f) => ({ ...f, location: { ...f.location, ...patch } }));

  // Validar CIF/NIF en backend con debounce
  useEffect(() => {
    if (form.customer_type !== "empresa") { setTaxIdStatus({ checking: false, valid: null }); return; }
    const clean = (form.tax_id || "").toUpperCase().replace(/[\s-]/g, "");
    if (clean.length < 9) { setTaxIdStatus({ checking: false, valid: null }); return; }
    setTaxIdStatus({ checking: true, valid: null });
    const t = setTimeout(async () => {
      try {
        const res = await api.post("/validate-tax-id", { tax_id: clean });
        setTaxIdStatus({ checking: false, valid: !!res.data.valid });
      } catch {
        setTaxIdStatus({ checking: false, valid: false });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.tax_id, form.customer_type]);

  // Geocodificación automática de la dirección (debounced)
  useEffect(() => {
    // Si ya hay GPS, no geocodificar
    if (form.location.lat) { setGeocoding({ checking: false, result: null }); return; }
    const addr = (form.location.address || "").trim();
    if (addr.length < 5) { setGeocoding({ checking: false, result: null }); return; }
    setGeocoding({ checking: true, result: null });
    const t = setTimeout(async () => {
      try {
        const res = await api.post("/geocode", { address: addr });
        if (res.data.found) {
          setGeocoding({ checking: false, result: res.data });
        } else {
          setGeocoding({ checking: false, result: { found: false } });
        }
      } catch {
        setGeocoding({ checking: false, result: null });
      }
    }, 800);
    return () => clearTimeout(t);
  }, [form.location.address, form.location.lat]);

  // Autocompletado de direcciones
  useEffect(() => {
    if (form.location.lat) { setSuggestions([]); return; }
    const addr = (form.location.address || "").trim();
    if (addr.length < 4) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/address-suggest?q=${encodeURIComponent(addr)}`);
        setSuggestions(res.data.results || []);
      } catch {
        setSuggestions([]);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.location.address, form.location.lat]);

  const pickSuggestion = (s) => {
    updateLocation({
      address: `${s.primary}${s.secondary ? ", " + s.secondary : ""}`,
      lat: s.lat,
      lng: s.lng,
    });
    setSuggestions([]);
    setShowSuggestions(false);
    setGeocoding({ checking: false, result: { found: true, ...s } });
  };

  const fetchQuote = async () => {
    try {
      const res = await api.post("/quote", {
        issue_type: form.issue_type,
        vehicle_brand: form.vehicle_brand || "Otros",
        vehicle_model: form.vehicle_model || "—",
        vehicle_year: Number(form.vehicle_year),
        lat: form.location.lat,
        lng: form.location.lng,
        address: form.location.address,
        customer_type: form.customer_type || "particular",
      });
      setQuote(res.data);
      if (res.data.is_custom_quote) setPaymentOption("full");
    } catch {
      toast.error("Error al calcular precio");
    }
  };

  useEffect(() => {
    if (step === 4 && form.issue_type) fetchQuote();
    // eslint-disable-next-line
  }, [step, form.location.address, form.location.lat, form.location.lng, form.customer_type]);

  const getGps = () => {
    if (!navigator.geolocation) return toast.error("GPS no disponible");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        updateLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeocoding({ checking: false, result: null });
        setSuggestions([]);
        toast.success("Ubicación capturada");
        setLoading(false);
      },
      () => {
        toast.error("No se pudo obtener ubicación");
        setLoading(false);
      }
    );
  };

  const canNext = () => {
    if (step === 0) {
      if (form.customer_type === "particular") return true;
      if (form.customer_type === "empresa") return taxIdStatus.valid === true;
      return false;
    }
    if (step === 1) return !!form.issue_type;
    if (step === 2) return form.vehicle_brand && form.vehicle_model && form.vehicle_year;
    if (step === 3) {
      const baseOk = form.location.address.length > 3 && form.customer_name && form.customer_phone;
      // Necesita GPS o geocoding válido para calcular distancia
      const hasLocation = form.location.lat || geocoding.result?.found;
      if (!hasLocation) return false;
      if (form.issue_type === "other_problem" && (form.notes || "").trim().length < 10) return false;
      return baseOk;
    }
    return true;
  };

  const submitAndPay = async () => {
    setLoading(true);
    try {
      const cleanTaxId = (form.tax_id || "").toUpperCase().replace(/[\s-]/g, "");
      const created = await api.post("/service-requests", {
        ...form,
        tax_id: form.customer_type === "empresa" ? cleanTaxId : null,
      });
      const checkout = await api.post("/checkout/create", {
        request_id: created.data.id,
        payment_option: paymentOption,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar pago");
      setLoading(false);
    }
  };

  const steps = [
    { n: 0, t: "Cliente" },
    { n: 1, t: "Avería" },
    { n: 2, t: "Vehículo" },
    { n: 3, t: "Ubicación" },
    { n: 4, t: "Precio y pago" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Toaster theme="dark" position="top-center" richColors />
      <Header />

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-16">
        {/* Progress */}
        <div className="flex items-center justify-between mb-10">
          {steps.map((s, i) => {
            let stepCircleCls = "border-white/20";
            if (step > s.n) stepCircleCls = "bg-[#FFC107] border-[#FFC107] text-black";
            else if (step === s.n) stepCircleCls = "border-[#FFC107] text-[#FFC107]";
            return (
            <div key={s.n} className="flex items-center flex-1">
              <div className={`flex items-center gap-2 ${step >= s.n ? "text-white" : "text-[#71717A]"}`}>
                <div
                  className={`w-7 h-7 rounded-full border flex items-center justify-center text-xs font-mono font-medium ${stepCircleCls}`}
                >
                  {step > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
                </div>
                <span className="hidden sm:inline text-xs font-mono uppercase tracking-wider">{s.t}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-px mx-3 ${step > s.n ? "bg-[#FFC107]" : "bg-white/10"}`} />}
            </div>
          );
          })}
        </div>

        <div key={step} className="step-enter">
          {step === 0 && (
            <div>
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-2">¿Eres particular o empresa?</h1>
              <p className="text-[#A1A1AA] mb-8 text-sm">Si tu vehículo está a nombre de una empresa, tienes <span className="text-[#FFC107] font-medium">-10% sobre el servicio</span>.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  onClick={() => update({ customer_type: "particular", tax_id: "" })}
                  className={`sc-card rounded-md p-6 text-left transition-all ${form.customer_type === "particular" ? "!border-[#FFC107] bg-[#FFC107]/5" : ""}`}
                  data-testid="customer-particular"
                >
                  <div className={`w-11 h-11 rounded-md flex items-center justify-center mb-3 ${form.customer_type === "particular" ? "bg-[#FFC107] text-black" : "bg-white/5 text-[#FFC107]"}`}>
                    <span className="font-display font-bold text-lg">PF</span>
                  </div>
                  <h3 className="font-display font-semibold text-xl mb-1">Particular</h3>
                  <p className="text-sm text-[#A1A1AA]">Persona física. Tarifa estándar + IVA 21%.</p>
                </button>
                <button
                  onClick={() => update({ customer_type: "empresa" })}
                  className={`sc-card rounded-md p-6 text-left transition-all ${form.customer_type === "empresa" ? "!border-[#FFC107] bg-[#FFC107]/5" : ""}`}
                  data-testid="customer-empresa"
                >
                  <div className={`w-11 h-11 rounded-md flex items-center justify-center mb-3 ${form.customer_type === "empresa" ? "bg-[#FFC107] text-black" : "bg-white/5 text-[#FFC107]"}`}>
                    <span className="font-display font-bold text-lg">PJ</span>
                  </div>
                  <h3 className="font-display font-semibold text-xl mb-1">Empresa</h3>
                  <p className="text-sm text-[#A1A1AA]">Persona jurídica. <span className="text-[#FFC107]">-10% servicio</span> + IVA 21%.</p>
                </button>
              </div>

              {form.customer_type === "empresa" && (
                <div className="mt-6">
                  <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-2 block">NIF / CIF / NIE *</label>
                  <div className="relative">
                    {(() => {
                      let taxBorderCls = "border-white/10 focus:border-[#FFC107]";
                      if (taxIdStatus.valid === true) taxBorderCls = "border-[#10B981]";
                      else if (taxIdStatus.valid === false) taxBorderCls = "border-[#EF4444]";
                      return (
                    <input
                      type="text"
                      value={form.tax_id}
                      onChange={(e) => update({ tax_id: e.target.value.toUpperCase() })}
                      placeholder="Ej: B12345674 o 12345678Z"
                      maxLength={11}
                      className={`w-full bg-[#141414] border rounded-md px-4 py-3.5 pr-12 text-white focus:outline-none transition-colors ${taxBorderCls}`}
                      data-testid="tax-id-input"
                    />
                      );
                    })()}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {taxIdStatus.checking && <Loader2 className="w-4 h-4 animate-spin text-[#71717A]" />}
                      {!taxIdStatus.checking && taxIdStatus.valid === true && <CheckCircle2 className="w-5 h-5 text-[#10B981]" />}
                      {!taxIdStatus.checking && taxIdStatus.valid === false && (form.tax_id || "").length >= 9 && <span className="text-[#EF4444] text-sm font-mono">✗</span>}
                    </div>
                  </div>
                  <p className="text-xs mt-1.5">
                    {taxIdStatus.valid === true && <span className="text-[#10B981]">✓ NIF/CIF válido</span>}
                    {taxIdStatus.valid === false && (form.tax_id || "").length >= 9 && <span className="text-[#EF4444]">NIF/CIF no válido. Comprueba el dígito de control.</span>}
                    {taxIdStatus.valid === null && <span className="text-[#71717A]">Se valida automáticamente con el algoritmo oficial de Hacienda.</span>}
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div>
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-2">¿Qué te ha ocurrido?</h1>
              <p className="text-[#A1A1AA] mb-8 text-sm">Selecciona el caso que mejor describa tu situación.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {ISSUES.map((issue) => {
                  const Icon = ICONS[issue.icon];
                  const selected = form.issue_type === issue.id;
                  const isOther = issue.id === "other_problem";
                  return (
                    <button
                      key={issue.id}
                      onClick={() => update({ issue_type: issue.id })}
                      className={`sc-card rounded-md p-5 text-left transition-all ${selected ? "!border-[#FFC107] bg-[#FFC107]/5" : ""} ${isOther ? "sm:col-span-2 lg:col-span-1" : ""}`}
                      data-testid={`issue-${issue.id}`}
                    >
                      <div className={`w-10 h-10 rounded-md flex items-center justify-center mb-3 ${selected ? "bg-[#FFC107] text-black" : "bg-white/5 text-[#FFC107]"}`}>
                        <Icon className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <h3 className="font-display font-semibold text-lg">{issue.label}</h3>
                      <p className="text-xs text-[#A1A1AA] mt-1.5 leading-relaxed">{issue.desc}</p>
                      {isOther && (
                        <p className="text-[10px] font-mono uppercase tracking-wider text-[#FFC107] mt-3">Solo pagas desplazamiento</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-2">Datos del vehículo</h1>
              <p className="text-[#A1A1AA] mb-8 text-sm">Para enviar el cerrajero adecuado y calcular el precio exacto.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-2 block">Marca</label>
                  <select
                    value={form.vehicle_brand}
                    onChange={(e) => update({ vehicle_brand: e.target.value })}
                    className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 text-white focus:border-[#FFC107] outline-none"
                    data-testid="vehicle-brand"
                  >
                    <option value="">Selecciona marca...</option>
                    {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-2 block">Modelo</label>
                  <input
                    type="text"
                    value={form.vehicle_model}
                    onChange={(e) => update({ vehicle_model: e.target.value })}
                    placeholder="Ej: Golf GTI, Clio, A4..."
                    className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 text-white placeholder:text-[#71717A] focus:border-[#FFC107] outline-none"
                    data-testid="vehicle-model"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-2 block">Año</label>
                  <input
                    type="number"
                    min="1970" max={new Date().getFullYear()}
                    value={form.vehicle_year}
                    onChange={(e) => update({ vehicle_year: e.target.value })}
                    className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 text-white focus:border-[#FFC107] outline-none"
                    data-testid="vehicle-year"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-2">¿Dónde te encuentras?</h1>
              <p className="text-[#A1A1AA] mb-8 text-sm">Comparte tu ubicación. Calculamos automáticamente la distancia desde Tàrrega.</p>

              <button
                onClick={getGps}
                disabled={loading}
                className="w-full btn-outline rounded-md py-4 mb-4 flex items-center justify-center gap-2 font-medium"
                data-testid="gps-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                {form.location.lat ? `✓ GPS capturado (${form.location.lat.toFixed(4)}, ${form.location.lng.toFixed(4)})` : "Capturar mi ubicación GPS (más rápido)"}
              </button>

              <div className="text-center text-xs font-mono uppercase tracking-wider text-[#71717A] mb-4">— o escribe la dirección —</div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-2 block">Dirección exacta *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={form.location.address}
                      onChange={(e) => updateLocation({ address: e.target.value, lat: null, lng: null })}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
                      placeholder="Calle, número, ciudad..."
                      className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 pr-12 focus:border-[#FFC107] outline-none"
                      data-testid="address-input"
                      autoComplete="off"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      {geocoding.checking && <Loader2 className="w-4 h-4 animate-spin text-[#71717A]" />}
                      {!geocoding.checking && (form.location.lat || geocoding.result?.found) && <CheckCircle2 className="w-5 h-5 text-[#10B981]" />}
                      {!geocoding.checking && !form.location.lat && geocoding.result && !geocoding.result.found && (form.location.address || "").length >= 5 && <span className="text-[#EF4444] text-sm font-mono">✗</span>}
                    </div>

                    {/* Dropdown sugerencias */}
                    {showSuggestions && suggestions.length > 0 && !form.location.lat && (
                      <div className="absolute z-50 left-0 right-0 top-full mt-1 sc-card rounded-md overflow-hidden shadow-2xl">
                        <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-[#FFC107] bg-[#FFC107]/5 border-b border-[#FFC107]/10">
                          Toca una opción para seleccionarla
                        </div>
                        {suggestions.map((s, i) => (
                          <button
                            key={`${s.lat}-${s.lng}-${i}`}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                            onTouchEnd={(e) => { e.preventDefault(); pickSuggestion(s); }}
                            className="w-full text-left px-4 py-3 hover:bg-[#FFC107]/5 active:bg-[#FFC107]/10 border-b border-white/5 last:border-b-0 flex items-start gap-3 group"
                            data-testid={`suggestion-${i}`}
                          >
                            <MapPin className="w-4 h-4 text-[#71717A] group-hover:text-[#FFC107] flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-white truncate">{s.primary}</div>
                              {s.secondary && <div className="text-xs text-[#71717A] truncate">{s.secondary}</div>}
                            </div>
                            <span className="text-xs font-mono text-[#FFC107] flex-shrink-0">{s.distance_km} km</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {(form.location.lat || geocoding.result?.found) && (
                    <div className="mt-2 px-3 py-2.5 rounded-md bg-[#10B981]/5 border border-[#10B981]/20 text-xs flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />
                      <span className="text-[#A1A1AA] flex-1">
                        {geocoding.result?.found ? (
                          <>Verificada: <span className="text-white">{(geocoding.result?.display_name || geocoding.result?.primary || "").split(",").slice(0, 3).join(", ")}</span></>
                        ) : "Ubicación capturada por GPS"}
                      </span>
                      <span className="font-mono text-[#10B981] flex-shrink-0">{geocoding.result?.found ? `${geocoding.result?.distance_km} km` : "GPS"}</span>
                    </div>
                  )}
                  {!form.location.lat && !geocoding.result?.found && !geocoding.checking && (form.location.address || "").length >= 5 && suggestions.length === 0 && (
                    <p className="text-xs text-[#EF4444] mt-1.5">No hemos podido localizar esta dirección. Prueba a añadir la ciudad o usa el botón de GPS arriba.</p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-2 block">Nombre *</label>
                    <input
                      type="text"
                      value={form.customer_name}
                      onChange={(e) => update({ customer_name: e.target.value })}
                      className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 focus:border-[#FFC107] outline-none"
                      data-testid="customer-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-2 block">Teléfono *</label>
                    <input
                      type="tel"
                      value={form.customer_phone}
                      onChange={(e) => update({ customer_phone: e.target.value })}
                      className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 focus:border-[#FFC107] outline-none"
                      data-testid="customer-phone"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-2 block">Email (opcional)</label>
                  <input
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => update({ customer_email: e.target.value })}
                    className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 focus:border-[#FFC107] outline-none"
                    data-testid="customer-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-2 block">
                    {form.issue_type === "other_problem" ? "Describe tu problema *" : "Notas (opcional)"}
                  </label>
                  <textarea
                    rows={form.issue_type === "other_problem" ? 5 : 3}
                    value={form.notes}
                    onChange={(e) => update({ notes: e.target.value })}
                    placeholder={form.issue_type === "other_problem"
                      ? "Describe con detalle qué le sucede a tu vehículo. El técnico evaluará in situ y te dará un presupuesto personalizado."
                      : "Coche con niños, parking subterráneo..."}
                    className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 focus:border-[#FFC107] outline-none resize-none"
                    data-testid="notes-input"
                  />
                  {form.issue_type === "other_problem" && (
                    <p className="text-xs text-[#71717A] mt-1.5">Mínimo 10 caracteres. Cuanto más detalle, mejor podremos prepararnos.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-2">
                {quote?.custom_quote_needed
                  ? "Presupuesto personalizado"
                  : quote?.is_custom_quote ? "Solo desplazamiento" : "Tu presupuesto"}
              </h1>
              <p className="text-[#A1A1AA] mb-8 text-sm">
                {quote?.custom_quote_needed
                  ? "No tenemos precio publicado para este vehículo. Contáctanos por WhatsApp y te lo cerramos en minutos."
                  : quote?.is_custom_quote
                    ? "El técnico evaluará tu problema in situ y te dará un presupuesto personalizado."
                    : "Precio cerrado. Sin cargos sorpresa."}
              </p>

              {!quote ? (
                <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#FFC107]" /></div>
              ) : quote.custom_quote_needed ? (
                <div className="sc-card rounded-md p-6 mb-6" data-testid="custom-quote-panel">
                  <div className="flex items-start gap-3 mb-5">
                    <Info className="w-5 h-5 text-[#FFC107] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-display font-semibold text-base text-white mb-1">
                        {form.vehicle_brand} {form.vehicle_model} ({form.vehicle_year})
                      </p>
                      <p className="text-sm text-[#A1A1AA] leading-relaxed">
                        Cada vehículo requiere una llave/apertura específica. Como no tenemos tarifa publicada para el tuyo, te preparamos el presupuesto directamente por WhatsApp. Suele estar listo en menos de 15 minutos.
                      </p>
                    </div>
                  </div>
                  <a
                    href={quote.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-black font-semibold py-4 px-6 rounded-md transition-colors"
                    data-testid="whatsapp-cta"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Enviar mensaje por WhatsApp
                  </a>
                  <p className="text-xs text-center text-[#71717A] mt-3">
                    Te responderemos con el precio cerrado en el chat.
                  </p>
                </div>
              ) : (
                <>
                  {quote.is_custom_quote && (
                    <div className="mb-6 p-5 rounded-md bg-[#FFC107]/5 border border-[#FFC107]/30">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-[#FFC107] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-display font-semibold text-base text-white mb-1">Cómo funciona &quot;Otro problema&quot;</p>
                          <p className="text-sm text-[#A1A1AA] leading-relaxed">
                            Ahora pagas únicamente el <b className="text-white">desplazamiento ({quote.total.toFixed(2)}€)</b>. Nuestro técnico se traslada a tu ubicación, evalúa el problema según lo que has descrito y te entrega un presupuesto cerrado para el servicio. Si aceptas, ejecuta el trabajo; si no, el cargo del desplazamiento se mantiene.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="sc-card rounded-md p-6 mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs font-mono uppercase tracking-wider text-[#71717A]">
                        {quote.is_custom_quote ? "DESPLAZAMIENTO (IVA inc.)" : "TOTAL (IVA inc.)"}
                      </span>
                      <span className="font-display font-bold text-5xl text-amber-grad">{quote.total.toFixed(2)}€</span>
                    </div>
                    <div className="border-t border-white/5 pt-4 space-y-2 text-sm">
                      {!quote.is_custom_quote && (
                        <div className="flex justify-between"><span className="text-[#A1A1AA]">Servicio base</span><span className="font-mono">{quote.breakdown.base.toFixed(2)}€</span></div>
                      )}
                      {quote.breakdown.brand_surcharge > 0 && (<div className="flex justify-between"><span className="text-[#A1A1AA]">Recargo marca premium</span><span className="font-mono">+{quote.breakdown.brand_surcharge.toFixed(2)}€</span></div>)}
                      {quote.breakdown.year_surcharge > 0 && (<div className="flex justify-between"><span className="text-[#A1A1AA]">Vehículo reciente (2015+)</span><span className="font-mono">+{quote.breakdown.year_surcharge.toFixed(2)}€</span></div>)}
                      {quote.breakdown.company_discount > 0 && (<div className="flex justify-between"><span className="text-[#10B981]">Descuento empresa (-10%)</span><span className="font-mono text-[#10B981]">−{quote.breakdown.company_discount.toFixed(2)}€</span></div>)}
                      {quote.breakdown.distance > 0 && (
                        <div className="flex justify-between">
                          <span className="text-[#A1A1AA]">Desplazamiento ({quote.distance_km?.toFixed(1) || "—"} km × 1,50€)</span>
                          <span className="font-mono">{quote.breakdown.distance.toFixed(2)}€</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-white/5">
                        <span className="text-[#A1A1AA]">IVA 21%</span>
                        <span className="font-mono">+{quote.breakdown.iva.toFixed(2)}€</span>
                      </div>
                    </div>
                  </div>

                  {!quote.is_custom_quote && (
                    <div className="mb-6">
                      <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-3 block">Elige cómo pagar</label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <button
                          onClick={() => setPaymentOption("deposit")}
                          className={`sc-card rounded-md p-5 text-left ${paymentOption === "deposit" ? "!border-[#FFC107] bg-[#FFC107]/5" : ""}`}
                          data-testid="pay-deposit"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-display font-semibold text-lg">Reserva 30%</span>
                            {paymentOption === "deposit" && <CheckCircle2 className="w-5 h-5 text-[#FFC107]" />}
                          </div>
                          <div className="font-display font-bold text-2xl text-[#FFC107]">{quote.deposit_amount.toFixed(2)}€</div>
                          <p className="text-xs text-[#71717A] mt-2">Pagas ahora. El resto ({(quote.total - quote.deposit_amount).toFixed(2)}€) al finalizar.</p>
                        </button>
                        <button
                          onClick={() => setPaymentOption("full")}
                          className={`sc-card rounded-md p-5 text-left ${paymentOption === "full" ? "!border-[#FFC107] bg-[#FFC107]/5" : ""}`}
                          data-testid="pay-full"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-display font-semibold text-lg">Pago completo</span>
                            {paymentOption === "full" && <CheckCircle2 className="w-5 h-5 text-[#FFC107]" />}
                          </div>
                          <div className="font-display font-bold text-2xl text-[#FFC107]">{quote.total.toFixed(2)}€</div>
                          <p className="text-xs text-[#71717A] mt-2">Abonas el total ahora. Nada pendiente al terminar.</p>
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={submitAndPay}
                    disabled={loading}
                    className="w-full btn-amber py-4 rounded-md font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="submit-pay-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{quote.is_custom_quote ? `Pagar desplazamiento ${quote.total.toFixed(2)}€` : "Continuar al pago seguro"} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <p className="text-xs text-center text-[#71717A] mt-4">Pago procesado por Stripe · Tarjeta encriptada SSL</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-white/5">
          <button
            onClick={() => step > 0 ? setStep(step - 1) : navigate("/")}
            className="btn-outline px-5 py-2.5 rounded-md font-medium flex items-center gap-2 text-sm"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Inicio" : "Anterior"}
          </button>
          {step < 4 && (
            <button
              onClick={() => canNext() && setStep(step + 1)}
              disabled={!canNext()}
              className="btn-amber px-5 py-2.5 rounded-md font-semibold flex items-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="next-btn"
            >
              Siguiente <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
